import { getDealerName } from "@phn/entities/dealer-details";
import type {
  PPNDealer,
  PPNDealerSearchResult,
} from "@phn/entities/dealer-response";
import { splitLocale } from "@phn/entities/locale";
import { GlobalDataLayerService } from "@phn/services/analytics/global-data-layer-service";
import { navContentStore } from "@phn/state/nav-content-store";
import { routerStore } from "@phn/state/router-store";
import { EventActions } from "@phn/utility/constants";
import { addQueryParamsToUrl, isDesktop, isString } from "@phn/utility/helper";
import {
  getOpeningHours,
  sanitizeDealerHomepage,
} from "@phn/utility/helper/dealer-search-helper";
import { Component, Prop, h, State } from "@stencil/core";
import { Fragment, JSX } from "@stencil/core/internal";
import { back } from "@phn/services/routing-manager";
import { onDealerCardClick } from "../dealer-search-results/dealer-search-form-item-card";

@Component({
  tag: "phn-nd-dealer-card",
  styleUrl: "dealer-card.scss",
})
export class DealerCard {
  @Prop() dealer!: PPNDealerSearchResult;

  @Prop() locale!: string;

  @Prop() isFlyout?: boolean;

  @Prop() singleResult?: boolean = false;

  @State() showMore = false;

  render(): JSX.Element | null {
    const { language } = splitLocale(this.locale);
    const { ppnDealer, distance } = this.dealer;
    const name = getDealerName(ppnDealer, language);
    const {
      testDrive,
      finder,
      backToSearch,
      directions,
      homepage,
      lessButton,
      moreButton,
      contact,
    } = navContentStore.state.dealer.details;
    const { breakpoint } = routerStore.state;
    const openingHours = getOpeningHours(
      ppnDealer.contactDetails.contactOpeningHours,
    );
    const hasTestdrive =
      testDrive && isString(testDrive?.text) && isString(testDrive?.link);
    const { country } = splitLocale(this.locale);
    const street = getLocalized(this.locale, "street", ppnDealer);
    const city = getLocalized(this.locale, "city", ppnDealer);
    const { postalCode } = ppnDealer.address;
    const mapsLink =
      country?.toUpperCase() === "CN"
        ? `https://gaode.com/search?query=${ppnDealer.nameLocalized},${ppnDealer.addressLocalized.street},${ppnDealer.addressLocalized.city},${ppnDealer.addressLocalized.state}`
        : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
            street,
          )},${encodeURIComponent(postalCode)}+${encodeURIComponent(city)}`;

    return (
      <Fragment>
        <p class="dealer-name">{name}</p>
        <p class="flyout-address">
          {street}, {postalCode} {city}
        </p>

        <div class="desktop-address hover-element">
          <phn-p-icon
            class="route-icon"
            name="route"
            color="primary"
            aria="{ 'aria-label': 'Route icon' }"
          />
          <phn-p-icon
            class="pin-icon"
            name="pin"
            color="primary"
            aria="{ 'aria-label': 'Pin icon' }"
          />
          <a
            hide-label="true"
            href={mapsLink}
            target="_blank"
            onClick={(e) => {
              e.stopPropagation();
              trackLinkClick(
                "location",
                `${street} ${postalCode} ${city}`,
                mapsLink,
                ppnDealer,
              );
            }}
          >
            <span class="flyout-directions">{directions}</span>
            <span>{street}</span>
            <span>
              {postalCode} {city}
            </span>
          </a>
          {distance && (
            <phn-p-tag
              compact="true"
              color="notification-info-soft"
            >{`${Math.round(
              distance.value,
            )}${distance.unit.toLowerCase()}`}</phn-p-tag>
          )}
        </div>

        <div class="dealer-homepage hover-element">
          <phn-p-icon
            name="globe"
            color="primary"
            aria="{ 'aria-label': 'Globe icon' }"
          />
          <a
            class="ml-2"
            href={sanitizeDealerHomepage(ppnDealer.contactDetails.homepage)}
            target="_blank"
            onClick={(e) => {
              e.stopPropagation();
              trackLinkClick(
                "website",
                ppnDealer.contactDetails.homepage,
                ppnDealer.contactDetails.homepage,
                ppnDealer,
              );
            }}
          >
            {homepage}
          </a>
        </div>

        <div class="dealer-finder flex hover-element">
          <phn-p-icon
            name="car"
            color="primary"
            aria="{ 'aria-label': 'Car icon' }"
          />
          <a
            href={addQueryParamsToUrl(finder.link, {
              dealership: ppnDealer.ppnOrgId,
            })}
            target="_blank"
            onClick={(e) => {
              e.stopPropagation();
              trackLinkClick(
                "finder",
                finder.text,
                (e.target as HTMLAnchorElement).href,
                ppnDealer,
              );
            }}
          >
            {finder.text}
          </a>
        </div>

        {(isDesktop(breakpoint) && this.showMore) || this.isFlyout ? (
          <Fragment>
            <p class="flyout-headline">{contact}</p>

            {ppnDealer.contactDetails.phoneNumber ? (
              <div class="dealer-phone hover-element">
                <phn-p-icon
                  name="phone"
                  color="primary"
                  aria="{ 'aria-label': 'Phone icon' }"
                />
                <a
                  dir="ltr"
                  href={`tel:${ppnDealer.contactDetails.phoneNumber}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    trackLinkClick(
                      "phone",
                      ppnDealer.contactDetails.phoneNumber!,
                      (e.target as HTMLAnchorElement).href,
                      ppnDealer,
                    );
                  }}
                >
                  {ppnDealer.contactDetails.phoneNumber}
                </a>
              </div>
            ) : null}

            <div class="dealer-mail hover-element">
              <phn-p-icon
                name="email"
                color="primary"
                aria="{ 'aria-label': 'Email icon' }"
              />
              <a
                href={`mailto:${ppnDealer.contactDetails.emailAddress}`}
                onClick={(e) => {
                  e.stopPropagation();
                  trackLinkClick(
                    "email",
                    ppnDealer.contactDetails.emailAddress!,
                    (e.target as HTMLAnchorElement).href,
                    ppnDealer,
                  );
                }}
              >
                {ppnDealer.contactDetails.emailAddress}
              </a>
            </div>

            {openingHours ? (
              <div class="dealer-opening-hours">
                <phn-p-icon
                  name="clock"
                  color="primary"
                  aria="{ 'aria-label': 'Clock icon' }"
                />
                <span>{openingHours}</span>
              </div>
            ) : null}

            {hasTestdrive ? (
              <div class="dealer-drive flex hover-element">
                <phn-p-icon
                  name="steering-wheel"
                  color="primary"
                  aria="{ 'aria-label': 'Steering wheel icon' }"
                />
                <a
                  class="ml-2"
                  href={testDrive.link}
                  onClick={(e) => {
                    e.stopPropagation();
                    trackLinkClick(
                      "testDrive",
                      testDrive.text,
                      testDrive.link,
                      ppnDealer,
                    );
                  }}
                >
                  {testDrive.text}
                </a>
              </div>
            ) : null}
          </Fragment>
        ) : null}

        {!this.isFlyout ? (
          <phn-p-button
            variant="secondary"
            class="dealer-more"
            data-testid="more-less-button"
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              if (isDesktop(breakpoint)) {
                this.showMore = !this.showMore;
                trackButtonClick(
                  this.showMore ? "more" : "less",
                  this.showMore ? moreButton : lessButton,
                  ppnDealer,
                );
              } else {
                /* istanbul ignore next */
                onDealerCardClick(ppnDealer);
              }
            }}
          >
            <div style={{ display: "flex" }}>
              <phn-p-icon
                name={this.showMore ? "arrow-head-up" : "arrow-head-down"}
              />
              {isDesktop(breakpoint) && this.showMore ? lessButton : moreButton}
            </div>
          </phn-p-button>
        ) : this.singleResult ? null : (
          <phn-p-button
            variant="secondary"
            class="flyout-back"
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              trackButtonClick("backToSearch", backToSearch, ppnDealer);
              back();
            }}
          >
            {backToSearch}
          </phn-p-button>
        )}
      </Fragment>
    );
  }
}

function trackLinkClick(
  clickElementId: string,
  clickElementName: string,
  targetUrl: string,
  dealer: PPNDealer,
) {
  const eventName = ["phone", "email"].includes(clickElementId)
    ? EventActions.DEALER_CONTACT_CLICK
    : EventActions.DEALER_LINK_CLICK;
  GlobalDataLayerService.getInstance().pushDataLayerEvent(eventName, {
    partner: {
      companyName: dealer.name,
      ppnOrgId: dealer.ppnOrgId,
      companyId: dealer.porschePartnerNo,
    },
    componentClick: {
      clickElementType: "link",
      clickElementId: `results.dealer.${clickElementId}`,
      clickElementName,
      targetUrl,
    },
  });
}

function trackButtonClick(
  clickElementId: string,
  clickElementName: string,
  dealer: PPNDealer,
) {
  GlobalDataLayerService.getInstance().pushDataLayerEvent(
    EventActions.BUTTON_CLICK,
    {
      partner: {
        companyName: dealer.name,
        ppnOrgId: dealer.ppnOrgId,
        companyId: dealer.porschePartnerNo,
      },
      componentClick: {
        clickElementType: "button",
        clickElementId: `results.dealer.${clickElementId}`,
        clickElementName,
      },
    },
  );
}

function getLocalized(
  locale: string,
  key: keyof PPNDealer["addressLocalized"],
  ppnDealer: PPNDealer,
): string {
  return (
    (!locale?.startsWith("en-") && ppnDealer.addressLocalized[key]) ||
    (ppnDealer.address[key] as string)
  );
}
